/**
 * NavLink - Next.js Link wrapper with Dashboard3 styling
 *
 * @example
 * ```tsx
 * <NavLink href="/classes">Go to Classes</NavLink>
 * ```
 */

"use client";

import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import {
  type CSSProperties,
  type PropsWithChildren,
  useMemo,
} from "react";

export type NavLinkProps = NextLinkProps &
  PropsWithChildren & {
    styles?: CSSProperties;
    borderRadius?: CSSProperties["borderRadius"];
  };

export const NavLink = ({
  href,
  children,
  styles,
  borderRadius,
  ...props
}: NavLinkProps) => {
  if (!href) {
    console.warn("⚠️ NavLink rendered without an href.");
    return <span>{children}</span>;
  }

  const memoizedStyles = useMemo(
    () => ({
      borderRadius: borderRadius || 0,
      ...styles,
    }),
    [borderRadius, styles],
  );

  return (
    <NextLink href={href} style={memoizedStyles} {...props}>
      {children}
    </NextLink>
  );
};

